import {ChangeDetectionStrategy, Component, Input} from '@angular/core';
import {BehaviorSubject} from 'rxjs';
import {
  HardwareManagementBetriebssystemStore,
  SOFTLINE_FEATURE_HARDWARE_MANAGEMENT_BETRIEBSSYSTEM
} from '../../../hardware-management.shared';
import {map} from 'rxjs/operators';
import {UntypedFormControl, UntypedFormGroup, FormGroupDirective, ReactiveFormsModule} from '@angular/forms';
import {
  MessageBarStore,
  SOFTLINE_FEATURE_MESSAGE_BAR,
  Validators,
} from '@softline/ui-core';
import { EntityInputComponent } from '@softline/ui-core/entity-input';
import {FocusMonitor} from '@angular/cdk/a11y';
import {Store} from '@softline/core';
import {showRequestErrors} from '@softline/application';
import {Betriebssystem} from '../../../types/betriebssystem';
import {CommonModule} from '@angular/common';
import {InputBaseComponent} from '../../input-base/input-base.component';

import { InputComponent } from '@softline/ui-core';
import { TranslatePipe } from '@softline/ui-core';
@Component({
    selector: 'lib-betriebssystem-input',
    imports: [CommonModule, InputBaseComponent, ReactiveFormsModule,
    InputComponent,
    TranslatePipe],
    templateUrl: './betriebssystem-input.component.html',
    styleUrls: ['./betriebssystem-input.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class BetriebssystemInputComponent {

  @Input() readonly = false;

  readonly betriebssystemInEditing$ = new BehaviorSubject<Betriebssystem | null>(null);

  readonly betriebssysteme$ = this.store.observe(
    SOFTLINE_FEATURE_HARDWARE_MANAGEMENT_BETRIEBSSYSTEM,
    HardwareManagementBetriebssystemStore.getters.all
  ).pipe(map(o => o as Betriebssystem[]));

  get formGroup(): UntypedFormGroup {
    return this.parentFormGroup.form;
  }

  readonly betriebssystemEditingForm = new UntypedFormGroup({
    betriebssystem: new UntypedFormControl(null, Validators.required()),
    bezeichnung: new UntypedFormControl(null),
    beschreibung: new UntypedFormControl(null)
  });

  constructor(
    private readonly parentFormGroup: FormGroupDirective,
    private readonly focusMonitor: FocusMonitor,
    private store: Store
  ) { }

  select(betriebssystem: Betriebssystem | null | undefined): void {
    if(!betriebssystem)
      return;
    this.formGroup.patchValue({ betriebssystem });
  }

  edit(entity: Betriebssystem | null | undefined): void {
    if(!entity)
      return;
    this.betriebssystemInEditing$.next(entity);
    this.betriebssystemEditingForm.patchValue(entity);
  }

  resetForm(betriebssystem?: Betriebssystem | null): void {
    if (!betriebssystem) {
      this.betriebssystemEditingForm.reset();
      return;
    }

    this.betriebssystemEditingForm.reset(betriebssystem);
  }

  async saveEdit(entity: Betriebssystem): Promise<void> {
    const updatedEntity = {
      ...entity,
      ...this.betriebssystemEditingForm.value
    };

    try {
      const result = await this.store.dispatch(
        SOFTLINE_FEATURE_HARDWARE_MANAGEMENT_BETRIEBSSYSTEM,
        HardwareManagementBetriebssystemStore.actions.create,
        { entity: updatedEntity }
      );

      this.select(result);
      this.betriebssystemEditingForm.reset();

      await this.store.dispatch(
        SOFTLINE_FEATURE_MESSAGE_BAR,
        MessageBarStore.actions.success,
        'Betriebssystem wurde erfolgreich angelegt'
      );
    } catch (e) {
      showRequestErrors(this.store, e);
    }
  }

  async create(entity: Betriebssystem): Promise<void> {
    try {
      const result = await this.store.dispatch(
        SOFTLINE_FEATURE_HARDWARE_MANAGEMENT_BETRIEBSSYSTEM,
        HardwareManagementBetriebssystemStore.actions.update,
        { entity }
      );

      this.select(result);
      this.betriebssystemEditingForm.reset();
      this.betriebssystemInEditing$.next(null);

      await this.store.dispatch(
        SOFTLINE_FEATURE_MESSAGE_BAR,
        MessageBarStore.actions.success,
        'Betriebssystem wurde erfolgreich aktualisiert'
      );
    } catch (e) {
      showRequestErrors(this.store, e);
    }
  }

  valueChanged(newValue: Betriebssystem | null | undefined): void {
    if (!newValue) {
      this.betriebssystemEditingForm.reset();
      this.betriebssystemInEditing$.next(null);
    }

    if (newValue && this.formGroup.value?.betriebssystem?.id !== newValue?.id) {
      this.formGroup.patchValue({ betriebssystem: newValue }, { onlySelf: true });
    }
  }

  canceled(entityInput?: EntityInputComponent<any>): void {
    this.formGroup.patchValue({ betriebssystem: null });
    this.betriebssystemEditingForm.reset();
    this.betriebssystemInEditing$.next(null);

    if (entityInput) {
      // remove any existing text of the input (reset)
      entityInput.onInput('');
      entityInput.input = '';
      entityInput.inputComponent.nativeElement.value = '';
    }
  }

  closed(): void {
    this.betriebssystemEditingForm.reset();
  }

  // We have to use an arrow-function in order to preserve `this` context
  readonly filterBetriebssystem = (betriebssysteme: Betriebssystem[], filter: string): Betriebssystem[] => {
    if (!filter || filter?.length < 1) {
      return betriebssysteme;
    }

    return betriebssysteme.filter(typ => {
      return typ.betriebssystem.toLowerCase().includes(filter)
        || typ.bezeichnung.toLowerCase().includes(filter)
        || typ.beschreibung.toLowerCase().includes(filter);
    });
  }
}
